import React, { useState, useMemo, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  FileWarning,
  Search,
  Send,
  X,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import TimesheetReports, { TIMESHEET_REPORT_TABS, type TimesheetReportTab } from './TimesheetReports';
import { EditedPill, ManualEntryPill } from '../../components/payroll/TimesheetDetailModal';
import { payGapNote } from '../../lib/pay-gap';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { getRoleLabel } from '../../lib/roles';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { workLocationLabel } from '../../constants/work-locations';
import { formatCalendarDate, formatInstantTime } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { fmtHours, sumMoney } from '../../lib/display-labels';
import { minutesOfRecord } from '../../lib/shift-hours';
import { isFlaggedShift, longShiftOf, longShiftLabel, otherAnomalyFlags } from '../../lib/long-shift';
import { useOpenRecord } from '../../pages/downhole/recordPage';
import { useUrlBackedText } from '../../hooks/useUrlBackedText';
import {
  QUEUE_PATH,
  hoursText,
  isUnsubmitted,
  queueRows,
  readQueueParams,
  useApproverAccess,
  useTimesheetQueue,
  writeQueueParams,
  type DateFilter,
  type QueueTab,
  type StatusTab,
  type Timesheet,
} from './approvalQueueModel';
import { shiftJobNumber } from '../../lib/shift-job-number';

/*
 * /payroll/approvals — the company's timesheets, full width. A row opens the
 * shift on its own page, /payroll/approvals/:timesheetId
 * (TimesheetApprovalScreen), where Approve, Reject and Edit live. It used to
 * open in a panel beside this list; split panes are banned (CLAUDE.md).
 *
 * The list's narrowing — status tab, date chip, name search — is in the URL
 * (`?status=`, `?date=`, `?q=`, approvalQueueModel.ts), so the record page's
 * back arrow returns to the list exactly as it was left, and an approve there
 * can move on to the next row of THIS list.
 */

/**
 * Refusals from POST /timesheets/:id/submit.
 *
 * `not_yours_to_submit` is the only coded one. The bare 404 — "Not found or not
 * in draft status" — is what a second click gets, because the UPDATE is
 * predicated on the status it is leaving, and it is worth saying so rather than
 * echoing a message that reads like the row went missing.
 */
const SUBMIT_CONFLICT_MESSAGES: Record<string, string> = {
  not_yours_to_submit:
    'Submitting someone else’s shift needs an approver role — admin, office staff or supervisor.',
};

/*
 * THE STANDING NOTE ABOVE THE UNSUBMITTED LIST IS GONE (fe#749), AND THE FACT
 * IT CARRIED IS RECORDED HERE INSTEAD, WHICH IS WHERE IT WAS ALWAYS USEFUL.
 *
 * `UNSUBMITTED_NOTE` read: clocked out but never handed in, clock-out submits
 * automatically since #594, so the list is normally empty and anything in it
 * predates that change or failed to auto-submit. Every word of that is true and
 * none of it is work — it describes how a row GOT here, which is the app's own
 * plumbing, not something the reader does anything about. The chip's count and
 * the rows themselves say what is waiting; the Submit button says what to do.
 *
 * It is not moved to a tooltip or a help icon. A fact a maintainer needs and a
 * reader does not belongs in the source, and this is the source.
 */

/* ── Helpers ── */
function nameHash(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  const hues = [210, 260, 330, 30, 160, 45, 0, 190];
  return `hsl(${hues[Math.abs(h) % hues.length]}, 55%, 55%)`;
}

function initials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/** An instant, on the tenant's wall clock (companies.time_zone) — never the device's. */
function fmtTime(iso: string | null | undefined, timeZone: string | null): string {
  if (!iso) return '--';
  return formatInstantTime(iso, timeZone, { hour: 'numeric', minute: '2-digit', hour12: true }, 'en-US') ?? '—';
}

/**
 * Running length of a shift that has not ended, from an instant to an instant.
 *
 * `clock_in_at` is a timestamptz and genuinely an instant, so `new Date()` is
 * right here for the same reason it is right in fmtTime and wrong in fmtDate.
 *
 * Returns null rather than a number for anything it cannot measure — an absent
 * clock-in, an unparseable one, or a start in the future (clock skew between the
 * hand's phone and the server). The caller renders '—'. A running duration is
 * exactly the kind of field that would otherwise show a confident '0h 00m' for a
 * shift nobody can account for.
 */
function elapsedMinutes(clockInAt: string | null | undefined, now: number): number | null {
  if (!clockInAt) return null;
  const started = new Date(clockInAt).getTime();
  if (!Number.isFinite(started)) return null;
  const mins = Math.floor((now - started) / 60_000);
  return mins >= 0 ? mins : null;
}

function fmtDuration(mins: number | null): string {
  if (mins === null) return '—';
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
}

/**
 * `entry_date` is a CALENDAR DAY — the twin of fmtDate in
 * src/components/payroll/TimesheetDetailModal.tsx, same column, same defect.
 *
 * This screen contradicted itself: isTenantToday() below already compares
 * `dateStr.slice(0, 10)` against todayCalendarDate() and is correct, so a
 * timesheet could be badged "Today" by the filter while the date column beside
 * the badge read yesterday. Both now read the same local day.
 *
 * fmtTime above reads clock_in_at / clock_out_at as instants, on the tenant's clock.
 */
function fmtDate(iso: string): string {
  return formatCalendarDate(iso, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '--';
}

/* ── Components ── */

function Avatar({ name, size = 40 }: { name: string; size?: number }) {
  return (
    <div
      className="flex items-center justify-center rounded-full font-bold text-white flex-shrink-0"
      style={{ width: size, height: size, backgroundColor: nameHash(name), fontSize: size * 0.38 }}
    >
      {initials(name)}
    </div>
  );
}

/**
 * The register badge and the card's status rail, in the house shape: a
 * SATURATED `--fi-signal-*` fill with dark ink on it, not the pale 10% wash
 * this screen used to draw. The signal hues have no light twin, so one dark
 * ink holds in both themes and the badge cannot go pale under the switch.
 *
 * EXHAUSTIVE over every value `status` can hold, plus the synthetic `flagged`
 * the caller substitutes when `anomaly_flags` is set. That was already true and
 * stays true: this was a chain of ternaries whose last branch was `: 'Pending'`,
 * so it labelled anything it did not recognise as Pending — and `draft` is a
 * value that reaches it. A draft row badged "Pending" would contradict the tab
 * it is sitting in and, worse, describe a shift the office has never been shown
 * as one waiting for their signature.
 *
 * FIVE STATES, FIVE HUES, so none is shared here — but the grouping rule still
 * applies and is worth stating: `flagged` and `rejected` are both red because
 * both mean "do not sign this as it stands", and the label separates them.
 */
const STATUS_BADGE: Record<string, { label: string; fill: string; rail: string }> = {
  draft: { label: 'Not submitted', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  submitted: { label: 'Pending', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  flagged: { label: 'Flagged', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  approved: { label: 'Approved', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  rejected: { label: 'Rejected', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};

/**
 * An unrecognised status claims NO colour and NO word.
 *
 * It must never fall back to a real status: on a queue whose whole job is to
 * say who has been signed for, "Approved" is a claim that someone's hours are
 * already counted. The raw value is not thrown away — it rides on the
 * accessible name, where it can be read and looked up — but it is not printed
 * as though it were a label, and the rail stays transparent so the row keeps
 * its width and claims nothing.
 */
const UNKNOWN_STATUS = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };

export const statusSkin = (status: string | null | undefined) =>
  (status && Object.prototype.hasOwnProperty.call(STATUS_BADGE, status) && STATUS_BADGE[status]) || UNKNOWN_STATUS;

function StatusPill({ status }: { status: string }) {
  const known = Object.prototype.hasOwnProperty.call(STATUS_BADGE, status);
  const skin = statusSkin(status);
  return (
    <span
      data-testid="timesheet-status-badge"
      /* The value is kept on the accessible name rather than in a `title`: a
         title attribute BECOMES the accessible name and would hide the visible
         word on every recognised status. */
      aria-label={known ? undefined : `Unrecognised status: ${status}`}
      className={`inline-flex w-fit items-center rounded-fi-pill border border-black/20 px-2 py-0.5 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

/* ── The register: one person, their shifts ── */

/**
 * THE LIST IS GROUPED BY PERSON, AND IT IS GROUPED WHETHER OR NOT THE PERSON
 * HAS SEVERAL SHIFTS (fe#749).
 *
 * The brief asked for grouping only where a name would otherwise repeat. It is
 * unconditional instead, because a list that switches between two card shapes
 * depending on how many rows a person happens to have today is a list whose
 * layout changes per tenant and per day — the same objection this repo's
 * collapse guidance makes to a row-count threshold. One shape reads the same
 * every time, and a single-shift person costs no extra height: the header takes
 * the line the card's own name row used to take.
 *
 * THE HEADER IS NOT A ROW. It carries no rail, no selection tint, no tabIndex
 * and no click handler, for the register idiom's reason: a group is not a
 * record, and anything that looks like a record on this screen looks like it
 * has a status someone could sign for. Each person is a named row group (a
 * `<tbody>` in the table, a group below md) so a reader — or a test — can ask
 * for one person's shifts without traversing the DOM.
 *
 * NOTHING COLLAPSES. Collapse was not asked for, and the default would be a
 * judgement this page has no evidence for yet; the tab is already narrowed by
 * status, by date and now by name. Said out loud so the next reader knows it
 * was a decision rather than an omission.
 */
interface PersonGroupData {
  /** Grouping identity: employee id AND name. See the docblock below. */
  key: string;
  /** The DOM id of the header's name node, referenced by each row's aria-labelledby. */
  nameId: string;
  name: string;
  rows: Timesheet[];
}

function groupByPerson(rows: Timesheet[]): PersonGroupData[] {
  const order: string[] = [];
  const byKey = new Map<string, PersonGroupData>();
  for (const ts of rows) {
    /*
      THE KEY IS THE ID *AND* THE NAME, AND THE SECOND HALF IS NOT BELT AND
      BRACES — IT IS THE ONLY THING THAT KEEPS A DISAGREEMENT VISIBLE.

      `employee_id` is the worker and the name is not: two hands can share a
      name, and keying on the name would put one person's header over another
      person's hours on the screen that decides whose hours get signed for. It
      is present as a UUID on every production row (`timesheets` has no
      `user_id` — see the Timesheet docblock), and `worker_name` comes off a
      join on it, so in production the pair is functionally determined and
      adding the name changes nothing.

      What it changes is the case where they DISAGREE. A group keyed on the id
      alone has to take its heading from one of its rows, and every other
      spelling then vanishes from the screen with nothing to say it did — one
      header, two people's hours under it, no symptom. Keying on the pair splits
      them instead: two headers, both names on screen, a reader who can see
      something is odd. Caught by a fixture that carried two names on one
      employee_id, which is the shape a mid-period rename would also take.

      A row that somehow carries no id becomes ITS OWN group keyed on the
      timesheet id, never a shared "unknown" bucket: absent is not a person, and
      collapsing every absent id into one group would invent a worker who has
      everybody's shifts. Same reason `[]` may not stand in for `undefined`
      anywhere else in this repo. `\u0000` separates the two halves because it
      cannot occur in either.
    */
    const key = ts.employee_id
      ? `${ts.employee_id}\u0000${ts.worker_name}`
      : `unidentified\u0000${ts.id}`;
    let g = byKey.get(key);
    if (!g) {
      /*
        The DOM id for the header's name node — the first half of every row's
        `aria-labelledby` — is taken from the group's FIRST TIMESHEET ID rather
        than from `key`, because `key` contains a name and a NUL and an HTML id
        may contain neither. A timesheet id is a UUID and a group always has at
        least one.
      */
      g = { key, nameId: `queue-person-${ts.id}`, name: ts.worker_name, rows: [] };
      byKey.set(key, g);
      order.push(key);
    }
    g.rows.push(ts);
  }
  /*
    First-appearance order, and no sort of our own in either direction. The API
    decides how this list is ordered and the screen does not get a second
    opinion on it; re-sorting here would be a client-side answer to a question
    the server already answered.
  */
  return order.map((k) => byKey.get(k)!);
}

/**
 * Opens a row on Enter or Space ONLY when the row itself has focus — the Rate
 * Sheets idiom (CLAUDE.md, "rows that contain inline action buttons"). An
 * Unsubmitted row holds a Submit button, and a key on that button is the
 * button's: a row handler that took Enter would open the record instead of
 * submitting it. For the same reason no row carries `role="button"`, which
 * would make Submit presentational.
 */
function rowKeys(open: () => void) {
  return (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open();
    }
  };
}

/**
 * The flags on a shift, one line each. The long-shift one is reworded from the
 * API's sentence. Below them, on an approved shift, why no pay was filed
 * (api#1148) — the title only; the record page carries the API's sentence.
 */
function ShiftFlags({ ts }: { ts: Timesheet }) {
  const longShift = longShiftOf(ts);
  const others = otherAnomalyFlags(ts.anomaly_flags);
  const gap = payGapNote(ts.pay_gap, ts.pay_gap_message);
  if (!longShift && others.length === 0 && !gap) return null;
  return (
    <div className="mt-1.5 space-y-1">
      {gap && (
        <div
          data-testid="pay-gap-marker"
          data-tone={gap.tone}
          className={`flex w-fit items-center gap-1.5 px-2 py-1 rounded-fi-control text-xs font-semibold text-fieldiq-text ${
            gap.tone === 'warning' ? 'bg-fi-amber/15' : 'bg-fieldiq-dark/60'
          }`}
        >
          <AlertTriangle size={13} aria-hidden="true" />
          {gap.title}
        </div>
      )}
      {longShift && (
        <div
          data-testid="long-shift-marker"
          className="flex w-fit items-center gap-1.5 px-2 py-1 rounded-fi-control text-xs font-semibold bg-fi-red/15 text-fieldiq-text"
        >
          <AlertTriangle size={13} aria-hidden="true" />
          {longShiftLabel(longShift)}
        </div>
      )}
      {others.map((flag) => (
        <div key={flag} className="flex w-fit items-center gap-1.5 px-2 py-1 rounded-fi-control text-xs font-medium bg-fi-red/15 text-fieldiq-text">
          <AlertTriangle size={13} aria-hidden="true" />
          {flag}
        </div>
      ))}
    </div>
  );
}

function SubmitButton({ onSubmit, isSubmitting }: { onSubmit: () => void; isSubmitting?: boolean }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onSubmit(); }}
      disabled={isSubmitting}
      className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-fi-control text-xs font-bold transition-colors disabled:opacity-40 bg-fi-amber/20 text-fieldiq-accent-signal hover:bg-fi-amber/30"
    >
      {isSubmitting ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
      Submit
    </button>
  );
}

interface RowProps {
  ts: Timesheet;
  /** The id of the group header's name node — the first half of this row's accessible name. */
  nameId: string;
  onOpen: () => void;
  /** Present only on the Unsubmitted tab. Absent elsewhere = no button at all. */
  onSubmit?: () => void;
  isSubmitting?: boolean;
  /** The table has a Submit column (the Unsubmitted tab), whether or not this row may use it. */
  actionColumn?: boolean;
}

/**
 * One shift, as a register row. Everything the old card said EXCEPT the name
 * and the avatar, which sit once on the person's header row above it.
 *
 * ITS ACCESSIBLE NAME STILL CARRIES THE WORKER, via `aria-labelledby` pointing
 * at the header's name node and then at this row's own content. Not an
 * `aria-label`: that would REPLACE the row's text, and the date, location,
 * times, hours and status would have to be spelled a second time here and kept
 * in step with the markup by hand.
 *
 * Shop vs field belongs on the APPROVAL row specifically. Approving is the
 * two-party attestation that lets a long shift into the safety denominator, so
 * the approver has to be able to see what they are signing for.
 * workLocationLabel renders NULL as an em dash rather than a location: every
 * row written before 0333 holds NULL, and printing "Field" there would invent a
 * fact about where someone worked.
 */
function ShiftTableRow({ ts, nameId, onOpen, onSubmit, isSubmitting, actionColumn = false }: RowProps) {
  const timeZone = useTenantTimeZone();
  const flagged = isFlaggedShift(ts);
  const skin = statusSkin(flagged ? 'flagged' : ts.status);
  const rowId = `shift-row-${ts.id}`;
  return (
    <tr
      id={rowId}
      data-shift-id={ts.id}
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={rowKeys(onOpen)}
      aria-labelledby={`${nameId} ${rowId}`}
      /* The 4px status rail on the ROW; an unknown status keeps the width and
         claims no colour. The one amber tint is focus, hover is neutral. */
      className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${skin.rail}`}
    >
      <td className="px-4 py-3 align-top text-sm">
        <span className="font-semibold text-fieldiq-text">{fmtDate(ts.entry_date)}</span>
        <ShiftFlags ts={ts} />
      </td>
      <td className="px-4 py-3 align-top text-sm text-fieldiq-muted">
        {workLocationLabel(ts.work_location)}
        {shiftJobNumber(ts) && ` · ${shiftJobNumber(ts)}`}
        {ts.project && ` · ${ts.project}`}
      </td>
      <td className="px-4 py-3 align-top text-sm text-fieldiq-muted whitespace-nowrap">
        {fmtTime(ts.clock_in_at, timeZone)} – {fmtTime(ts.clock_out_at, timeZone)}
      </td>
      <td className="px-4 py-3 align-top text-sm tabular-nums text-fieldiq-text whitespace-nowrap">{hoursText(ts)}</td>
      <td className="px-4 py-3 align-top">
        <span className="flex items-center gap-1.5">
          {ts.edited_after_submission === true && <EditedPill />}
          {ts.clock_source === 'manual' && <ManualEntryPill />}
          <StatusPill status={flagged ? 'flagged' : ts.status} />
        </span>
      </td>
      {actionColumn ? (
        <td className="px-4 py-3 align-top text-right" onClick={(e) => e.stopPropagation()}>
          {onSubmit && <SubmitButton onSubmit={onSubmit} isSubmitting={isSubmitting} />}
        </td>
      ) : null}
    </tr>
  );
}

/** The same shift below md, as a stacked card. Cards carry no rail. */
function ShiftCard({ ts, nameId, onOpen, onSubmit, isSubmitting }: RowProps) {
  const timeZone = useTenantTimeZone();
  const flagged = isFlaggedShift(ts);
  const bodyId = `shift-card-${ts.id}`;
  return (
    <div
      data-shift-id={ts.id}
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={rowKeys(onOpen)}
      aria-labelledby={`${nameId} ${bodyId}`}
      className="cursor-pointer rounded-fi-control border border-fieldiq-border bg-fieldiq-card p-3 outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10"
    >
      <div id={bodyId} className="min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="text-xs text-fieldiq-muted min-w-0">
            <span className="font-semibold text-fieldiq-text">{fmtDate(ts.entry_date)}</span>
            {' · '}{workLocationLabel(ts.work_location)}
            {shiftJobNumber(ts) && ` · ${shiftJobNumber(ts)}`}
            {ts.project && ` · ${ts.project}`}
          </p>
          <span className="flex items-center gap-1.5 flex-shrink-0">
            {ts.edited_after_submission === true && <EditedPill />}
            {ts.clock_source === 'manual' && <ManualEntryPill />}
            <StatusPill status={flagged ? 'flagged' : ts.status} />
          </span>
        </div>
        <p className="text-xs mt-1 text-fieldiq-muted">
          {fmtTime(ts.clock_in_at, timeZone)} – {fmtTime(ts.clock_out_at, timeZone)} · <span className="text-fieldiq-text">{hoursText(ts)}</span>
        </p>
        <ShiftFlags ts={ts} />
      </div>
      {onSubmit && (
        <div className="mt-3">
          <SubmitButton onSubmit={onSubmit} isSubmitting={isSubmitting} />
        </div>
      )}
    </div>
  );
}

/**
 * The person's header row: one cell spanning every column, carrying the
 * heading row's transparent 4px rail so the columns under it line up. A group
 * is not a record — no tabIndex, no click, no tint, and the rail claims no
 * colour, or it would read as a status for the whole group.
 *
 * THE COUNT IS RENDERED ONLY WHEN THERE IS MORE THAN ONE, and it is in the
 * group's accessible name in BOTH cases. On screen, "1" beside every single
 * shift is noise on a list the reader is scanning for the multi-shift people;
 * to a screen reader, "Dale Rigg, 1 shift" and "Dale Rigg, 3 shifts" are the
 * same sentence with the number that matters in it. The visible number is
 * `aria-hidden` so it is not spoken twice.
 */
function PersonHeader({ group, nameId }: { group: PersonGroupData; nameId?: string }) {
  const n = group.rows.length;
  return (
    <div className="flex items-center gap-2">
      <Avatar name={group.name} size={28} />
      <p id={nameId} className="min-w-0 flex-1 truncate text-sm font-semibold text-fieldiq-text">
        {group.name}
      </p>
      {n > 1 && (
        <span
          aria-hidden="true"
          className="flex-shrink-0 rounded-fi-pill border border-fieldiq-border bg-fieldiq-card px-2 py-0.5 text-[0.68rem] font-black tabular-nums text-fieldiq-muted"
        >
          {n}
        </span>
      )}
    </div>
  );
}

const groupLabel = (g: PersonGroupData) => `${g.name}, ${g.rows.length} ${g.rows.length === 1 ? 'shift' : 'shifts'}`;

/**
 * The name filter, above the list and OUTSIDE its scroll container so it stays
 * put while the list moves under it.
 *
 * IT NARROWS THE CURRENT TAB ONLY, and it narrows nothing else: the chip
 * counts, the band's four figures and the "N remaining" an approve reports are
 * all tallies of the queue, not of what is on screen. A search that quietly
 * moved them would let "0 remaining" mean "nobody matching Dale is left" on a
 * queue with thirty-nine other people waiting — which is the reassuring-zero
 * this screen's every other figure is written to avoid.
 */
function NameSearch({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="relative">
      <Search
        size={14}
        aria-hidden="true"
        className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-fieldiq-muted"
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Search this tab by name"
        placeholder="Search by name"
        /* The webkit cancel button is turned off because this input already has
           a clear control of its own, two lines down. Left on, Chrome draws a
           second, unlabelled ✕ beside it — and the one the reader is most likely
           to hit is the one no screen reader can name. */
        className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card py-1.5 pl-8 pr-8 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none [&::-webkit-search-cancel-button]:appearance-none"
      />
      {value !== '' && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear name search"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-fieldiq-muted hover:text-fieldiq-text"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}

/**
 * A shift that is still running. Deliberately NOT a TimesheetCard: it is not
 * selectable and carries no action, because there is nothing yet to act on —
 * an open shift has no end time, so it cannot be submitted and cannot be
 * approved. Making it look like the queue cards would be an invitation to click
 * something that is not there.
 */
function OnTheClockCard({ ts, now }: { ts: Timesheet; now: number }) {
  const timeZone = useTenantTimeZone();
  const mins = elapsedMinutes(ts.clock_in_at, now);
  return (
    <div
      /*
        Named rather than left anonymous. The card carries no control, so there
        is no button or link to find it by, and "the element containing this
        worker's name" would match the queue card for the same worker on another
        tab. A group with the worker's name is what lets a reader — assistive
        tech or a test — ask for THIS card.
      */
      role="group"
      aria-label={`${ts.worker_name} — on the clock`}
      className="rounded-fi-panel p-4 bg-fieldiq-card border border-fi-green/25"
    >
      <div className="flex items-start gap-3">
        <Avatar name={ts.worker_name} size={40} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-semibold truncate text-fieldiq-text">
              {ts.worker_name}
            </p>
            <span className="flex items-center gap-1.5 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none px-2 py-0.5 rounded-fi-pill flex-shrink-0 border border-black/20 bg-fi-signal-green text-fi-signal-ink">
              <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse bg-fi-signal-ink" />
              On the clock
            </span>
          </div>
          <p className="text-xs mt-0.5 text-fieldiq-muted">
            {fmtDate(ts.entry_date)} · {workLocationLabel(ts.work_location)}
            {shiftJobNumber(ts) && ` · ${shiftJobNumber(ts)}`}
            {ts.project && ` · ${ts.project}`}
          </p>
          <p className="text-xs mt-1 text-fieldiq-muted">
            {/*
              No end time and no dash standing in for one. "Since 6:02 AM" is the
              whole truth about an open shift; "6:02 AM – --" invites the reader
              to look for the missing half.
            */}
            Since {fmtTime(ts.clock_in_at, timeZone)}
          </p>
          <p className="text-lg font-bold mt-2 tabular-nums text-fieldiq-text">
            {fmtDuration(mins)}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * The Unsubmitted twin of NoApproverAccessNotice, and it says something
 * DIFFERENT on purpose — the two gates are not the same list. An elevated
 * dispatcher gets the approve buttons and not the submit ones, because
 * `submitTimesheet`'s on-behalf branch is the strict role list. It sits above
 * the list, beside the Submit buttons it explains.
 */
function NoSubmitAccessNotice({ role }: { role?: string }) {
  return (
    <div className="flex items-start gap-2 px-4 py-3 rounded-fi-panel text-sm border border-fi-amber/30 bg-fi-amber/10 text-fieldiq-accent-signal">
      <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
      <span>
        These shifts were clocked out but never handed in. Submitting one on a
        worker's behalf takes an admin, office staff or supervisor
        {role ? ` — yours is ${getRoleLabel(role)}` : ''}. Your own shifts still
        show a Submit button.
      </span>
    </div>
  );
}

/* ── Success Banner ── */
function SuccessBanner({ message }: { message: string }) {
  return (
    <div
      role="status"
      className="flex items-center gap-2 px-4 py-3 rounded-fi-panel text-sm font-medium border border-fi-green/30 bg-fi-green/10 text-fieldiq-text"
    >
      <CheckCircle2 size={16} />
      {message}
    </div>
  );
}

/**
 * The two halves of this screen: the status-scoped list, and the period-scoped
 * reports that aggregate over the same rows. Kept in the URL (`?view=reports&
 * report=shop`) so a report is linkable and /payroll/reports can redirect to one.
 */
type ScreenView = 'list' | 'reports';

function readReportTab(v: string | null): TimesheetReportTab {
  return TIMESHEET_REPORT_TABS.some((t) => t.key === v) ? (v as TimesheetReportTab) : 'hours';
}

/**
 * ── THE BAND MEETS A REAL TAB STRIP, AND THIS IS THE ANSWER ─────────────────
 *
 * CLAUDE.md carried this as an OPEN QUESTION: no page had ever had both the
 * page-header band and a tab strip, and the two candidates named — Field
 * Tickets and Dispatch — had not been re-skinned. Timesheets is the first, and
 * the answer is the one the standard already implies:
 *
 *   THE TAB STRIP GOES IN THE BODY, DIRECTLY UNDER THE BAND, ABOVE EVERYTHING
 *   ELSE. It is not on the band and it is not inside the register panel.
 *
 * WHY NOT ON THE BAND. Everything drawn on `--fi-brand` has to be spelled in
 * constant white opacities, because the band stays navy in both themes — so a
 * tab strip there would need its own permanently-navy active/inactive
 * treatment, separate from every other tab strip in the app, and the hero
 * circle's measured clearance was swept against stat text only. A strip on the
 * band is a second thing crossing the circle that nobody has measured.
 *
 * WHY THIS IS A TAB STRIP AND NOT THE QUOTES CHIP ROW. fe#704 turned
 * /finance/invoices' underlined "tabs" into chips and settled that case, and
 * the test it settled is what SWITCHES:
 *
 *   chips  narrow ONE list. Every chip answers the same question about the same
 *          rows, so each can carry a count, and the register underneath keeps
 *          its shape. The status row below is exactly this.
 *   tabs   switch WHICH QUESTION the page is answering. Timesheets and
 *          Timesheet reports do not share a list, a scope or a control —
 *          reports are pay-period-scoped, the list is status-scoped (fe#688) —
 *          and a count on "Timesheet reports" would be a count of nothing.
 *
 * So the page carries both shapes at once, one under the other, and they do not
 * read as the same control: the strip is underlined and unnumbered, the chips
 * are filled pills with counts.
 *
 * `aria-pressed` rather than the full tab/tablist/tabpanel pattern, kept from
 * the shape this replaced: the two views are separate URLs (`?view=`), each
 * independently linkable, which is a set of toggle buttons rather than a
 * roving-tabindex widget over one panel.
 */
function ViewStrip({ view, onChange }: { view: ScreenView; onChange: (v: ScreenView) => void }) {
  const items: { key: ScreenView; label: string }[] = [
    { key: 'list', label: 'Timesheets' },
    { key: 'reports', label: 'Timesheet reports' },
  ];
  return (
    <div
      data-testid="timesheet-view-strip"
      role="group"
      aria-label="Switch view"
      className="flex gap-1 border-b border-fieldiq-border"
    >
      {items.map((it) => (
        <button
          key={it.key}
          onClick={() => onChange(it.key)}
          aria-pressed={view === it.key}
          className={`relative px-3 py-2 text-sm transition-colors ${
            view === it.key
              ? 'font-extrabold text-fieldiq-text'
              : 'font-semibold text-fieldiq-muted hover:text-fieldiq-text'
          }`}
        >
          {it.label}
          {view === it.key && (
            <span aria-hidden="true" className="absolute bottom-0 left-0 right-0 h-[2px] bg-fi-amber" />
          )}
        </button>
      ))}
    </div>
  );
}

/**
 * The status row, in the body as the standard's filter chips rather than as the
 * tab strip it used to be inside the 320px queue panel.
 *
 * THE OLD SHAPE HAD A REAL DEFECT THIS FIXES BY MOVING. Six entries did not fit
 * 320px on one line and the two that fell off the end — Rejected and On the
 * clock — were clipped with no scrollbar and no way to reach them; the strip
 * was made to wrap. In the body it has the page's full width and the chips
 * scroll sideways rather than wrapping.
 *
 * A COUNT IS ABSENT, NOT ZERO, WHEN THE LIST DID NOT ARRIVE. These are tallies
 * of the one list this screen loads, so a refused or failed read leaves every
 * one of them unknown — and "Pending 0" on a queue is the reassuring sentence
 * this whole family of bugs is about. `displayValue` renders each as a dash.
 */
function QueueChips({
  tabs,
  active,
  onTheClockCount,
  onChange,
}: {
  tabs: { key: StatusTab; label: string; count: number | undefined }[];
  active: QueueTab;
  onTheClockCount: number | undefined;
  onChange: (tab: QueueTab) => void;
}) {
  const chip = (isActive: boolean) =>
    `inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
      isActive
        ? 'bg-fi-amber text-fi-amber-ink'
        : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
    }`;

  return (
    <div
      className="scrollbar-hide flex gap-2 overflow-x-auto"
      role="group"
      aria-label="Filter by status"
    >
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          aria-pressed={active === tab.key}
          onClick={() => onChange(tab.key)}
          className={chip(active === tab.key)}
        >
          {tab.label}
          <span
            data-testid={`queue-chip-count-${tab.key}`}
            className={`tabular-nums ${active === tab.key ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}
          >
            {displayValue(tab.count)}
          </span>
        </button>
      ))}

      {/*
        Separated by a rule and not amber when inactive, because it is not one
        of the four. The other chips are places work waits for someone; this one
        is a window on what is happening. Same row so it is findable, visibly
        not a queue so nobody works it.

        No `title`. A title attribute BECOMES the accessible name and hides the
        visible one, so this button announced itself as "Shifts running right
        now" and the word on screen — the word anyone would say out loud — was
        not its name. The sentence lives on the view it opens instead.
      */}
      <span aria-hidden="true" className="mx-1 my-1.5 w-px flex-shrink-0 bg-fieldiq-border" />
      <button
        type="button"
        aria-pressed={active === 'on_the_clock'}
        onClick={() => onChange('on_the_clock')}
        className={
          active === 'on_the_clock'
            ? 'inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition bg-fi-signal-green text-fi-signal-ink'
            : chip(false)
        }
      >
        <Clock size={12} />
        On the clock
        <span
          data-testid="queue-chip-count-on_the_clock"
          className={`tabular-nums ${active === 'on_the_clock' ? 'text-fi-signal-ink' : 'text-fieldiq-muted'}`}
        >
          {displayValue(onTheClockCount)}
        </span>
      </button>
    </div>
  );
}

/* ── Main Screen ── */
interface ListState {
  /** "… approved — 8.00h · 0 remaining", from the record page's last approve. */
  flash?: unknown;
}

export default function ApprovalQueueScreen() {
  const queryClient = useQueryClient();
  const timeZone = useTenantTimeZone();
  const navigate = useNavigate();
  const location = useLocation();
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const view: ScreenView = searchParams.get('view') === 'reports' ? 'reports' : 'list';
  const reportTab = readReportTab(searchParams.get('report'));
  /*
    THE LIST'S NARROWING IS IN THE URL (`?status=`, `?date=`, `?q=`), not in
    component state, because a row now opens its own page and the back arrow
    has to come back to the list exactly as it was left. Switching to the
    reports and back keeps it, as the state it replaced did.
  */
  const queue = readQueueParams(searchParams);
  const statusTab = queue.status;
  const dateFilter = queue.date;
  const nameQuery = queue.q;
  const setQueue = (next: Partial<typeof queue>, replace = false) =>
    setSearchParams(writeQueueParams(searchParams, { ...queue, ...next }), { replace });
  const setView = (v: ScreenView) => {
    const next = new URLSearchParams(searchParams);
    if (v === 'reports') { next.set('view', 'reports'); next.set('report', reportTab); }
    else { next.delete('view'); next.delete('report'); }
    setSearchParams(next);
  };
  const setReportTab = (t: TimesheetReportTab) => {
    const next = new URLSearchParams(searchParams);
    next.set('view', 'reports');
    next.set('report', t);
    setSearchParams(next, { replace: true });
  };
  /* Each keystroke replaces the entry, so Back does not step through a name letter by letter. */
  const setNameQuery = (q: string) => setQueue({ q }, true);
  /* The box keeps its own text and writes it through; see useUrlBackedText. */
  const [nameInput, onNameInput] = useUrlBackedText(nameQuery, setNameQuery);
  const setDateFilter = (date: DateFilter) => setQueue({ date });

  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const showSuccess = useCallback((msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 5000);
  }, []);
  /* The banner an approve on the record page carried back here, when it was the last row. */
  React.useEffect(() => {
    const flash = (location.state as ListState | null)?.flash;
    if (typeof flash === 'string') showSuccess(flash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  /**
   * Drives the running duration on the On the clock cards. Ticks a minute at a
   * time because the number is rendered to the minute — a per-second interval
   * would re-render sixty times for each visible change.
   *
   * The list itself refetches on its own 30s cadence; this only moves the
   * clock between those, so a shift that ended is still gone within 30s.
   */
  const [now, setNow] = useState(() => Date.now());
  React.useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  /**
   * ONE request, unfiltered, and that is deliberate rather than a leftover.
   *
   * GET /timesheets takes a `status` query param, so `status=draft` IS
   * expressible server-side — but it would not separate the two tabs this
   * screen needs, because a running shift and a clocked-out-never-submitted
   * shift are BOTH `draft`. The only thing that tells them apart is
   * `clock_out_at`, and the endpoint has no filter for it.
   *
   * WHAT THE API OWES, if the draft set ever stops being small: a filter on the
   * open/closed split — `clock_out=open|closed`, or an `on_the_clock=true` —
   * on GET /timesheets. Until then, splitting here costs nothing that a second
   * request would not cost more.
   *
   * `= []` IS THE TRAP, NOT A CONVENIENCE. The default turns every failure of
   * this call into an empty queue, and the empty copy below then says "Nothing
   * unsubmitted — every clocked-out shift has been handed in." That is an
   * affirmative claim about payroll completeness, made from a request that
   * never arrived. The default stays (every derivation below reads the array)
   * and the error is carried alongside it, so the list can tell the three
   * states apart.
   */
  const timesheetsQuery = useTimesheetQueue();
  const { data: rawTimesheets = [], isLoading } = timesheetsQuery;
  // A query waiting to retry reports `error: null`. See `queryLoadFailure`.
  const timesheetsFailure = queryLoadFailure(timesheetsQuery);

  const { canSubmitForOthers } = useApproverAccess();

  // Global counts (unfiltered by date)
  const allTimesheets = rawTimesheets as Timesheet[];
  const submittedCount = useMemo(() => allTimesheets.filter((t) => t.status === 'submitted').length, [allTimesheets]);
  const approvedCount = useMemo(() => allTimesheets.filter((t) => t.status === 'approved').length, [allTimesheets]);
  const rejectedCount = useMemo(() => allTimesheets.filter((t) => t.status === 'rejected').length, [allTimesheets]);

  /**
   * The split, and the reason the two predicates are written as each other's
   * complement on `clock_out_at` rather than on two different statuses.
   *
   * `openShifts` asks only "has this shift ended", with no status in it at all.
   * A shift with no clock-out cannot have been submitted or approved — both
   * transitions run off a closed row — so status adds nothing here, and leaving
   * it out is what makes "an open shift never appears in Unsubmitted" true by
   * construction instead of by two filters agreeing with each other.
   *
   * NO DURATION FILTER on `unsubmitted`, deliberately. Five of TNT's twenty rows
   * are zero minutes: three are clock-in tests and two are Derek Blattner's
   * Aug 31 field rows, kept on purpose as an audit trail. Which is which is the
   * office's call — a UI that hid them would be making it, invisibly.
   */
  const openShifts = useMemo(
    () => allTimesheets.filter((t) => !t.clock_out_at),
    [allTimesheets],
  );
  const unsubmitted = useMemo(() => allTimesheets.filter(isUnsubmitted), [allTimesheets]);
  const unsubmittedCount = unsubmitted.length;
  const onTheClockCount = openShifts.length;

  /*
    The tab and date chip (`tabRows`), then the name search (`timesheets`) —
    see `queueRows` for why the two are kept apart.
  */
  const { tabRows, rows: timesheets } = useMemo(
    () => queueRows(allTimesheets, queue, timeZone),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allTimesheets, queue.status, queue.date, queue.q, timeZone],
  );

  const people = useMemo(() => groupByPerson(timesheets), [timesheets]);

  /**
   * THE BAND'S FOUR FIGURES AND THE CHIP COUNTS — tallies of the ONE list this
   * screen already loads, with no added query.
   *
   * `null` WHILE IT IS IN FLIGHT AND WHENEVER IT DID NOT ARRIVE, which is the
   * whole reason this is a guarded object rather than the bare `.length`
   * counts above it. Those read `0` off the `= []` default, so a refused or
   * dropped list would paint "Pending 0 · Running 0 · Unsubmitted 0" across the
   * band — a confident, reassuring, entirely unfounded claim that nobody is
   * waiting on this office. displayValue() and fmtHours() render every one of
   * these as a dash.
   *
   * THE HOURS FIGURE IS ALL-OR-NOTHING, for the reason fe#709 and fe#710 made
   * the money figures all-or-nothing: a sum that silently skips the rows whose
   * hours of record are absent is an understatement that looks exactly like a
   * correct figure. `sumMoney` returns null the moment any contributor is
   * absent. (An empty loaded queue really is 0.00h, and `pending` beside it
   * reads 0.)
   */
  const tally = useMemo(() => {
    if (isLoading || timesheetsFailure) return null;
    const pendingRows = allTimesheets.filter((t) => t.status === 'submitted');
    const pendingMinutes = sumMoney(pendingRows.map((t) => minutesOfRecord(t.payable_minutes, t.raw_minutes)));
    return {
      pending: submittedCount,
      // One unreadable row makes the whole figure a dash, never a smaller number.
      hours: pendingMinutes === null ? null : pendingMinutes / 60,
      running: onTheClockCount,
      unsubmitted: unsubmittedCount,
    };
  }, [isLoading, timesheetsFailure, allTimesheets, submittedCount, onTheClockCount, unsubmittedCount]);

  /*
    CLEARS THE NAME on a tab change and keeps the date. A query left standing
    over a tab the reader has just switched to would show them an empty
    Unsubmitted list with the reason two inches above the fold. A date chip and
    the name box are two narrowings of the SAME list and compose on purpose.
  */
  const handleTabChange = (tab: QueueTab) => setQueue({ status: tab, q: '' });

  const openShift = (id: string) => openRecord(`${QUEUE_PATH}/${id}`);

  /**
   * One row, one request, one audit row. There is no bulk submit and there
   * should not be: each of these is the office deciding, for one shift, that
   * hours nobody handed in are real enough to go to an approver. `submitted_by`
   * records who decided, and the approve route then bars that person from being
   * the only signature on it if they are not a trusted role.
   */
  const submitMutation = useMutation({
    mutationFn: (id: string) => api.timesheets.submit(id),
    onSuccess: (_data, id) => {
      const ts = unsubmitted.find((t) => t.id === id);
      showSuccess(
        `${ts?.worker_name ?? 'Timesheet'} — ${hoursText(ts)} submitted. It is in Pending now.`,
      );
    },
    onError: (err: any) => {
      const code = err?.response?.data?.code;
      const serverMsg = err?.response?.data?.error;
      toast.error(
        (code && SUBMIT_CONFLICT_MESSAGES[code])
          || serverMsg
          || 'Could not submit this timesheet.',
      );
    },
    // onSettled: a refused submit leaves the row where the list last described
    // it, and a click that changes nothing on screen is indistinguishable from
    // one that never registered. The detail's key is ['timesheet', id],
    // singular, which the list prefix does not match, so it is named too.
    onSettled: (_data, _err, id) => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] });
      queryClient.invalidateQueries({ queryKey: ['timesheet', id] });
    },
  });

  const statusTabs: { key: StatusTab; label: string; count: number | undefined }[] = [
    { key: 'unsubmitted', label: 'Unsubmitted', count: tally ? unsubmittedCount : undefined },
    { key: 'submitted', label: 'Pending', count: tally ? submittedCount : undefined },
    { key: 'approved', label: 'Approved', count: tally ? approvedCount : undefined },
    { key: 'rejected', label: 'Rejected', count: tally ? rejectedCount : undefined },
  ];

  const dateFilters: { key: DateFilter; label: string }[] = statusTab === 'submitted'
    ? [{ key: 'all', label: 'All' }, { key: 'today', label: 'Today' }, { key: 'week', label: 'This week' }, { key: 'flagged', label: 'Flagged' }]
    : [{ key: 'all', label: 'All' }, { key: 'today', label: 'Today' }, { key: 'week', label: 'This week' }];

  const PANEL_TITLES: Record<QueueTab, string> = {
    unsubmitted: 'Unsubmitted',
    submitted: 'Pending review',
    approved: 'Approved',
    rejected: 'Rejected',
    on_the_clock: 'On the clock',
  };

  const EMPTY_COPY: Record<StatusTab, string> = {
    unsubmitted: 'Nothing unsubmitted — every clocked-out shift has been handed in.',
    submitted: 'No timesheets to review',
    approved: 'No approved timesheets',
    rejected: 'No rejected timesheets',
  };

  /*
    Own row or an approver role. `undefined` renders no button at all rather
    than a disabled one: a Submit the viewer can never use is a control that
    says the office is stuck, when what is true is that this person is not the
    one who does it. The notice above the list says who is.
  */
  const submitFor = (ts: Timesheet) =>
    statusTab === 'unsubmitted' && (canSubmitForOthers || ts.employee_id === user?.id)
      ? () => submitMutation.mutate(ts.id)
      : undefined;
  const isSubmittingRow = (ts: Timesheet) => submitMutation.isPending && submitMutation.variables === ts.id;
  const actionColumn = statusTab === 'unsubmitted';

  const bandStats = [
    {
      icon: <ClipboardCheck className="size-4" aria-hidden="true" />,
      label: 'Pending',
      sub: 'Waiting on a signature',
      tone: 'accent' as const,
      loading: isLoading,
      value: displayValue(tally?.pending),
    },
    {
      icon: <Clock className="size-4" aria-hidden="true" />,
      label: 'Hours',
      sub: 'In the pending queue',
      tone: 'info' as const,
      loading: isLoading,
      value: tally?.hours == null ? '—' : `${fmtHours(tally.hours)}h`,
    },
    /* The third label is 'Running' (well under the ~70px the hero circle's
       clearance was measured for — the circle's edge crosses this column
       between 1024 and 1048). The full phrase lives on the sub-line, which is
       the Equipment precedent: shorten the stat, leave the page's own names
       alone. The chip below still reads "On the clock". */
    {
      icon: <Activity className="size-4" aria-hidden="true" />,
      label: 'Running',
      sub: 'On the clock now',
      tone: 'success' as const,
      loading: isLoading,
      value: displayValue(tally?.running),
    },
    /* THE SUB-LINE IS SHORT BECAUSE THE SWEEP SAID SO, not for tidiness.
       "Clocked out, not handed in" put the fourth sub-line under the circle's
       28% edge at 1256–1432 wide, reading 3.78–4.00. "Never handed in" clears
       at every width. */
    {
      icon: <FileWarning className="size-4" aria-hidden="true" />,
      label: 'Unsubmitted',
      sub: 'Never handed in',
      tone: 'neutral' as const,
      loading: isLoading,
      value: displayValue(tally?.unsubmitted),
    },
  ];

  return (
    <div data-testid="timesheets-page" className="animate-fade-in min-h-full flex flex-col bg-fieldiq-dark">
      {/* The app shell renders the one global <Header />. Pages never do. */}
      <PageHeaderBand
        eyebrow="Payroll cycle"
        title="Timesheets"
        onBack={() => navigate(-1)}
        backLabel="Back"
        /* The four figures describe the whole queue, not the tab in view, and
           they stay put across BOTH views. See `tally` for why each is absent
           rather than zero when the list did not arrive. */
        stats={bandStats}
      />

      <section className="flex flex-1 flex-col px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto flex w-full max-w-[1920px] flex-1 flex-col space-y-4">
          <ViewStrip view={view} onChange={setView} />

          {/*
            The reports are a SIBLING of the list, not a child of it: the list's
            status chips and date chips do not apply to them, and their
            pay-period picker lives inside TimesheetReports so it cannot reach
            the list.
          */}
          {view === 'reports' ? (
            <TimesheetReports tab={reportTab} onTabChange={setReportTab} />
          ) : (
            <>
              <QueueChips
                tabs={statusTabs}
                active={statusTab}
                onTheClockCount={tally ? onTheClockCount : undefined}
                onChange={handleTabChange}
              />

              {/* The date narrowing is a SECOND question over the same rows,
                  so it is its own control rather than four more status chips.
                  It carries no counts: nothing returns them. */}
              {statusTab !== 'on_the_clock' && (
                <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by date">
                  <span className="text-xs text-fieldiq-muted">Date</span>
                  {dateFilters.map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      aria-pressed={dateFilter === f.key}
                      onClick={() => setDateFilter(f.key)}
                      className={`rounded-fi-pill border px-3 py-1.5 text-xs font-bold transition-colors ${
                        dateFilter === f.key
                          ? 'border-fi-amber bg-fieldiq-card text-fieldiq-text'
                          : 'border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              )}

              {successMsg && <SuccessBanner message={successMsg} />}
              {statusTab === 'unsubmitted' && !canSubmitForOthers && (
                <NoSubmitAccessNotice role={user?.role} />
              )}

              {/*
                THE REGISTER, full width. A row opens the shift on its own page,
                where it is approved, rejected or corrected.
              */}
              <section
                data-testid="timesheet-register"
                aria-labelledby="timesheet-register-title"
                className="flex flex-col rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
              >
                <header className="border-b border-fieldiq-border px-4 py-4">
                  <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">
                    Approval queue
                  </p>
                  <h2
                    id="timesheet-register-title"
                    className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text"
                  >
                    {PANEL_TITLES[statusTab]}
                  </h2>
                </header>

                {statusTab === 'on_the_clock' ? (
                  /*
                    On the clock is a full-width board of live cards, not rows:
                    an open shift has nothing to sign and nothing to open. The
                    pulsing green badge and the running duration on each card
                    say the whole of it.
                  */
                  <div className="p-4">
                    {isLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 size={24} className="animate-spin text-fieldiq-muted" />
                      </div>
                    ) : timesheetsFailure ? (
                      <LoadFailureNotice
                        error={queryFailureError(timesheetsQuery)}
                        kind={timesheetsFailure}
                        subject="timesheets"
                      />
                    ) : openShifts.length === 0 ? (
                      <p className="text-sm py-12 text-center text-fieldiq-muted">
                        Nobody is clocked in.
                      </p>
                    ) : (
                      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
                        {openShifts.map((ts) => (
                          <OnTheClockCard key={ts.id} ts={ts} now={now} />
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div data-testid="approval-queue-list">
                    {/*
                      IT RENDERS ONLY ONCE THE LIST HAS ARRIVED. A filter over a
                      list that is still in flight, or one the server refused,
                      is a control that says there is something to narrow — and
                      typing into it would produce an empty result
                      indistinguishable from a real one. It stays rendered on a
                      genuinely EMPTY tab, because a reader who has typed a
                      query has to be able to clear it.
                    */}
                    {!isLoading && !timesheetsFailure && (
                      <div className="border-b border-fieldiq-border p-3">
                        <div className="max-w-sm">
                          <NameSearch value={nameInput} onChange={onNameInput} />
                        </div>
                      </div>
                    )}

                    <div data-testid="approval-queue-rows">
                      {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 size={24} className="animate-spin text-fieldiq-muted" />
                        </div>
                      ) : timesheetsFailure ? (
                        /* THE THIRD BRANCH, where the empty state would have
                           been — not a banner above it. A refused list is not
                           "nothing unsubmitted". */
                        <LoadFailureNotice
                          error={queryFailureError(timesheetsQuery)}
                          kind={timesheetsFailure}
                          subject="timesheets"
                          className="px-4 py-12"
                        />
                      ) : timesheets.length === 0 ? (
                        /*
                          A FOURTH STATE, AND IT IS NOT THE EMPTY ONE. An empty
                          tab is a fact about the data and a no-match is a fact
                          about what the reader typed. Both print a blank list,
                          so saying which is which is the only thing that keeps
                          the tab's sentence from being an unfounded claim.
                        */
                        tabRows.length > 0 ? (
                          <p className="py-12 px-4 text-center text-sm text-fieldiq-muted">
                            No one in this tab matches “{nameQuery.trim()}”.
                            {' '}Its {tabRows.length} {tabRows.length === 1 ? 'shift is' : 'shifts are'} still here.
                          </p>
                        ) : (
                          <p className="text-sm text-center py-12 text-fieldiq-muted">
                            {EMPTY_COPY[statusTab as StatusTab]}
                          </p>
                        )
                      ) : (
                        <>
                          <div className="hidden overflow-x-auto md:block">
                            <table className="w-full min-w-[820px] text-left">
                              <thead>
                                <tr className="border-l-4 border-l-transparent text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">
                                  <th scope="col" className="px-4 py-2.5">Date</th>
                                  <th scope="col" className="px-4 py-2.5">Where</th>
                                  <th scope="col" className="px-4 py-2.5">Shift</th>
                                  <th scope="col" className="px-4 py-2.5">Hours</th>
                                  <th scope="col" className="px-4 py-2.5">Status</th>
                                  {actionColumn && <th scope="col" className="px-4 py-2.5"><span className="sr-only">Submit</span></th>}
                                </tr>
                              </thead>
                              {people.map((group) => (
                                <tbody key={group.key} aria-label={groupLabel(group)} data-testid="approval-queue-person">
                                  <tr className="border-t border-l-4 border-fieldiq-border border-l-transparent bg-fieldiq-dark/40">
                                    <th scope="rowgroup" colSpan={actionColumn ? 6 : 5} className="px-4 py-2 text-left font-normal">
                                      <PersonHeader group={group} nameId={group.nameId} />
                                    </th>
                                  </tr>
                                  {group.rows.map((ts) => (
                                    <ShiftTableRow
                                      key={ts.id}
                                      ts={ts}
                                      nameId={group.nameId}
                                      onOpen={() => openShift(ts.id)}
                                      onSubmit={submitFor(ts)}
                                      isSubmitting={isSubmittingRow(ts)}
                                      actionColumn={actionColumn}
                                    />
                                  ))}
                                </tbody>
                              ))}
                            </table>
                          </div>

                          <div className="space-y-4 p-3 md:hidden">
                            {people.map((group) => (
                              <div key={group.key} role="group" aria-label={groupLabel(group)}>
                                <div className="px-1 pb-2">
                                  <PersonHeader group={group} nameId={`${group.nameId}-card`} />
                                </div>
                                <div className="space-y-2">
                                  {group.rows.map((ts) => (
                                    <ShiftCard
                                      key={ts.id}
                                      ts={ts}
                                      nameId={`${group.nameId}-card`}
                                      onOpen={() => openShift(ts.id)}
                                      onSubmit={submitFor(ts)}
                                      isSubmitting={isSubmittingRow(ts)}
                                    />
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
